import Vue from 'vue';
import _ from 'lodash';
import axios from 'axios';
import log from './logger';
import AppConfig from '@/config';
import { events } from 'pdk/services';


export class HttpServiceFactory {
    create(options = {}) {
        const api = axios.create({
            baseURL: options.baseURL || AppConfig.api.baseURL || '/api',
            responseType: options.responseType || 'json',
            headers: options.headers || {
                'portalid': AppConfig.context.portalID
            }
        });

        // API Extensions
        api.all = function (promises) {
            return Promise.all(promises);
        };
        api.spread = function (callback) {
            return function wrap(arr) {
                return callback.apply(null, arr);
            };
        };


        // Authorization Header Interceptor
        api.interceptors.request.use(async (config) => {
            // Add the authorization header (if we have an access token)
            if (!AppConfig.env.testing && !config.headers['authorization']) {
                await Vue.$auth.updateToken(AppConfig.auth.refreshThreshold);
                config.headers['authorization'] = `Bearer ${Vue.$auth.token}`;
            }

            return config;
        });


        // Logging Interceptor
        api.interceptors.response.use(
            function (response) {

                // Log the call (in dev mode)
                if (!AppConfig.env.production && AppConfig.logging.ajax) {
                    const message = `%cajax ${response.config.url}`;

                    console.groupCollapsed(message, 'color: #4CAF50; font-weight: bold');
                    console.log('%c config', 'color: #9E9E9E; font-weight: bold', response.config);
                    console.log('%c request', 'color: #03A9F4; font-weight: bold', response.request);
                    console.log('%c response', 'color: #4CAF50; font-weight: bold', response);
                    console.groupEnd();
                }

                // Normalize the headers
                if (!response.headers) response.headers = {};

                return response;
            },
            function (error) {
                events.publish(`error.http`, error);
                
                // Do not log/handle network errors, they are out of our control and we do not need to be alerted.
                if (error.message === 'Network Error') 
                    return Promise.reject(error);
                    
                // The request was made and the server responded with a status code that falls out of the accepted range
                if (error.response) {
                    events.publish(`${error.response.status}.error.http`, error);

                    if (error.response.status === 401) {
                        switch (error.response.data) {
                            case 'AuthenticationTokenMissing':
                            case 'AuthenticationTokenInvalid':
                            case 'AuthenticationTokenExpired':
                                Vue.$auth.clearToken();
                                break;
                        }
                    }

                    // Determine if we should ignore the logging for this response
                    const ignoreCondition = _.get(error, 'config.logging.ignore');
                    let isLoggingIgnored = false;

                    if (typeof ignoreCondition !== 'undefined') {
                        if (_.isArray(ignoreCondition) && _.includes(ignoreCondition, error.response.status)) isLoggingIgnored = true;
                        else if (_.isNumber(ignoreCondition) && ignoreCondition == error.response.status) isLoggingIgnored = true;
                        else if (_.isString(ignoreCondition) && ignoreCondition == error.response.status.toString()) isLoggingIgnored = true;
                        else if (_.isFunction(ignoreCondition) && ignoreCondition(error) === true) isLoggingIgnored = true;
                    }

                    // Log the call (if applicable)
                    if (!isLoggingIgnored && _.includes([401, 403, 404, 405, 408, 418], error.response.status)) {
                        if (!AppConfig.env.production && AppConfig.logging.ajax) {
                            const message = `%cajax ${error.response.config.url}`;

                            console.groupCollapsed(message, 'color: #f44336; font-weight: bold');
                            console.log('%c config', 'color: #9E9E9E; font-weight: bold', error.response.config);
                            console.log('%c request', 'color: #03A9F4; font-weight: bold', error.response.request);
                            console.log('%c response', 'color: #f44336; font-weight: bold', error.response);
                            console.log('%c error', 'color: #f44336; font-weight: bold', error);
                            console.groupEnd();
                        }

                        log.error({
                            message: typeof error.response === 'string' ? error.response : error.message,
                            action: error.config.method,
                            callsite: error.config.url,
                            logger: "HttpService",
                            exception: error.message
                        });
                    }
                }

                // The request was made but no response was received
                else if (error.request) {
                    // Log the call (in dev mode)
                    if (!AppConfig.env.production && AppConfig.logging.ajax) {
                        const message = `%cajax failed`;

                        console.groupCollapsed(message, 'color: #f44336; font-weight: bold');
                        console.log('%c request', 'color: #03A9F4; font-weight: bold', error.request);
                        console.log('%c error', 'color: #f44336; font-weight: bold', error);
                        console.groupEnd();
                    }

                    log.error({
                        message: "Ajax Failed", 
                        action: error.config.method, 
                        callsite: error.config.url,
                        logger: "HttpService",
                        exception: error.message
                    });
                }

                // Something happened in setting up the request that triggered an Error
                else {
                    log.error({
                        message: "Failed setting up Ajax request", 
                        action: null, 
                        callsite: null,
                        logger: "HttpService",
                        exception: error.message
                    });
                }

                return Promise.reject(error);
            }
        );

        return api;
    }
}

export function kendoBeforeSend(req){
    req.setRequestHeader('authorization', `Bearer ${Vue.$auth.token}`);

    req.setRequestHeader('portalid', AppConfig.context.portalID);
}

export default new HttpServiceFactory().create();
